import { useState } from "react";
import { Link } from "react-router-dom";
import AwsActivateForm from "../components/AwsActivateForm";
import AwsActivateHeroVisual from "../components/AwsActivateHeroVisual";
import {
  AWS_ACTIVATE_HERO_STATS,
  AWS_BENEFITS,
  AWS_ELIGIBILITY,
  AWS_FAQS,
  AWS_MORE_SERVICES,
  AWS_PROCESS_STEPS,
  AWS_WHY_EMICRON,
} from "../data/awsActivateContent";
import { CONTACT_PHONE, CONTACT_PHONE_TEL } from "../data/constants";

function SectionHead({ label, title, desc, center = false }) {
  return (
    <div className={`aws-section-head${center ? " aws-section-head--center" : ""}`}>
      {label ? <span className="aws-section-label">{label}</span> : null}
      <h2 className="aws-section-title">{title}</h2>
      {desc ? <p className="aws-section-desc">{desc}</p> : null}
      <div className="aws-section-line" aria-hidden="true" />
    </div>
  );
}

export default function AwsActivatePage() {
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToForm = () => {
    document.getElementById("aws-apply-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="aws-activate-page">
      <section className="aws-hero animate-on-scroll">
        <div className="aws-hero-glow aws-hero-glow--orange" aria-hidden="true" />
        <div className="aws-hero-glow aws-hero-glow--green" aria-hidden="true" />
        <div className="container position-relative">
          <div className="row g-4 g-lg-5 align-items-center">
            <div className="col-lg-6">
              <span className="aws-badge">
                <i className="bi bi-cloud-check" aria-hidden="true" />
                AWS Activate Provider
              </span>
              <h1 className="aws-hero-title">
                Empower Your Startup with{" "}
                <span className="aws-hero-accent">AWS Activate Credits</span>
              </h1>
              <p className="aws-hero-tagline">Build Faster. Scale Smarter. Spend Less on Infrastructure.</p>
              <p className="aws-hero-desc">
                Emicron helps eligible startups access up to $100,000 in AWS Activate credits
                along with expert cloud architecture, security, and cost optimization guidance.
              </p>
              <div className="aws-hero-actions">
                <button type="button" className="aws-btn aws-btn-primary" onClick={scrollToForm}>
                  Apply for AWS Credits
                  <i className="bi bi-arrow-right" aria-hidden="true" />
                </button>
                <Link to="/contact" className="aws-btn aws-btn-outline">
                  Schedule Free Consultation
                </Link>
              </div>
            </div>
            <div className="col-lg-6">
              <AwsActivateHeroVisual />
            </div>
          </div>
          <div className="aws-hero-stats">
            {AWS_ACTIVATE_HERO_STATS.map((item) => (
              <div key={item.label} className="aws-hero-stat">
                <span className="aws-hero-stat-icon">
                  <i className={`bi ${item.icon}`} aria-hidden="true" />
                </span>
                <span className="aws-hero-stat-label">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="aws-section aws-eligibility animate-on-scroll" id="benefits">
        <div className="container">
          <SectionHead
            label="Check Your Startup Eligibility"
            title="Eligibility Requirements"
            desc="Ensure your startup meets the AWS Activate criteria before applying."
            center
          />
          <div className="row g-4">
            {AWS_ELIGIBILITY.map((item) => (
              <div key={item.title} className="col-md-6 col-lg-4">
                <article className="aws-eligibility-card">
                  <div className="aws-eligibility-card-top">
                    <span className="aws-icon-box">
                      <i className={`bi ${item.icon}`} aria-hidden="true" />
                    </span>
                    <span className="aws-eligibility-badge">{item.badge}</span>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="aws-section aws-section--soft animate-on-scroll">
        <div className="container">
          <SectionHead
            title="What You Gain with Our Support"
            desc="End-to-end guidance from application to cloud optimization — not just credits."
            center
          />
          <div className="row g-4">
            {AWS_BENEFITS.map((item) => (
              <div key={item.title} className="col-md-6 col-lg-4">
                <article className="aws-benefit-card">
                  <span className="aws-icon-box aws-icon-box--lg">
                    <i className={`bi ${item.icon}`} aria-hidden="true" />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="aws-section aws-form-section animate-on-scroll" id="aws-apply-form">
        <div className="container">
          <SectionHead
            label="Application"
            title="Start Your Application"
            desc="Follow the steps to complete your AWS Activate application with Emicron."
          />
          <AwsActivateForm />
        </div>
      </section>

      <section className="aws-section aws-section--soft animate-on-scroll" id="how-it-works">
        <div className="container">
          <SectionHead label="Application Process" title="How It Works" center />
          <div className="aws-timeline">
            {AWS_PROCESS_STEPS.map((item, idx) => (
              <div key={item.title} className="aws-timeline-step">
                <div className="aws-timeline-node">
                  <span className="aws-timeline-num">{String(idx + 1).padStart(2, "0")}</span>
                  <i className={`bi ${item.icon}`} aria-hidden="true" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="aws-section animate-on-scroll">
        <div className="container">
          <SectionHead title="Why Startups Choose Emicron" center />
          <div className="aws-why-grid">
            {AWS_WHY_EMICRON.map((item) => (
              <div key={item.title} className="aws-why-item">
                <span className="aws-icon-box aws-icon-box--sm">
                  <i className={`bi ${item.icon}`} aria-hidden="true" />
                </span>
                <span>{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="aws-section aws-section--soft animate-on-scroll" id="resources">
        <div className="container">
          <SectionHead title="Frequently Asked Questions" center />
          <div className="row g-3">
            {AWS_FAQS.map((item, idx) => (
              <div key={item.q} className="col-md-6">
                <div className={`aws-faq-item${openFaq === idx ? " is-open" : ""}`}>
                  <button
                    type="button"
                    className="aws-faq-trigger"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    aria-expanded={openFaq === idx}
                  >
                    <span className="aws-faq-q">{item.q}</span>
                    <i className={`bi bi-chevron-${openFaq === idx ? "up" : "down"}`} aria-hidden="true" />
                  </button>
                  {openFaq === idx && <p className="aws-faq-a">{item.a}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="aws-section animate-on-scroll">
        <div className="container">
          <SectionHead title="Explore More Cloud Services" center />
          <div className="aws-services-grid">
            {AWS_MORE_SERVICES.map((item) => (
              <Link key={item.label} to="/services" className="aws-service-item">
                <span className="aws-icon-box aws-icon-box--sm">
                  <i className={`bi ${item.icon}`} aria-hidden="true" />
                </span>
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="aws-footer-cta animate-on-scroll">
        <div className="aws-footer-cta-glow" aria-hidden="true" />
        <div className="container position-relative">
          <div className="aws-footer-cta-card">
            <div className="row g-4 align-items-center">
              <div className="col-lg-2 d-none d-lg-flex justify-content-center">
                <div className="aws-footer-cta-icon" aria-hidden="true">
                  <i className="bi bi-rocket-takeoff" />
                </div>
              </div>
              <div className="col-lg-6">
                <span className="aws-footer-cta-badge">Get Started Today</span>
                <h2>Ready to Accelerate Your Startup on AWS?</h2>
                <p>Apply today and get expert support from Emicron&apos;s AWS-certified team throughout your Activate journey.</p>
              </div>
              <div className="col-lg-4">
                <div className="aws-footer-cta-actions">
                  <button type="button" className="aws-btn aws-btn-primary" onClick={scrollToForm}>
                    Apply Now
                  </button>
                  <a href={`tel:${CONTACT_PHONE_TEL}`} className="aws-btn aws-btn-outline-light">
                    Talk to an AWS Expert
                  </a>
                  <span className="aws-footer-phone">{CONTACT_PHONE}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
